import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCommand } from '../../context/CommandContext';
import { usePlan } from '../../context/PlanContext';
import { useUserRole } from '../../hooks/useUserRole';
import { useSpeechToText, isSpeechRecognitionSupported } from '../../hooks/useSpeechToText';
import UniversalExportModal from '../UniversalExportModal';
import FederationWizard from '../FederationWizard';
import SuggestionChips from '../SuggestionChips';
import ConversationThreads from '../ConversationThreads';
import { COMMAND_MODAL_CONFIGS } from '../../utils/constants';
import { BACKEND_URL } from '../../config';

import {
  ArrowsClockwise,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Clock,
  Crown,
  Eye,
  Info,
  Lightning,
  Microphone,
  MicrophoneSlash,
  PaperPlaneRight,
  Phone,
  Play,
  Robot,
  Shield,
  WarningCircle,
  X,
  ArrowRight,
  FlowArrow,
  SkipForward,
  Hammer,
  Wrench,
  Sparkle,
  Database,
  CheckSquare,
  MagnifyingGlass,
  Plus,
  Minus,
  Bell,
  EnvelopeSimple,
  Plugs,
  PaperPlaneTilt,
  Trash,
  GearSix,
  ChatText,
  TreeStructure,
  Stack,
  Cube,
  CircleHalf,
  Target,
  SpeakerHigh,
  SpeakerSimpleSlash,
  SpeakerSimpleHigh,
  Stop
} from '@phosphor-icons/react';

import { APP_SLUG_MAP, getSlug, connectApp, checkConnectionStatus, openOAuthPopup } from '../../utils/composioAuth';
import { getAppLogo } from '../../utils/usedApps';

// ═══════════════════════════════════════════════════════════════
// IMPORTS FROM MODULAR CONSTANTS
// ═══════════════════════════════════════════════════════════════
import {
  MSG, nextId, createMessage, buildWelcomeMessages,
  STATUS_ICON_MAP, STATUS_COLOR_MAP,
  CREATION_STEPS, CLONE_PHASES,
  CATEGORY_COLORS, CATEGORY_ICONS,
  EXEC_STATUS_STYLES, QUICK_ACTION_CATALOG,
  CHANNEL_ICONS,
} from './constants';


// Sub-components extracted to SubComponents.jsx
import {
  ActionChipRow,
  CommandBarMic,
  ExecutionMessage,
  OrchestrationMessage,
  CreationMessage,
  WorkflowChangeCard,
  CompactResultCard,
  QuickActionChips,
  CloneTemplateMessage,
  RunExplanationCard,
  CapabilityPlanCard,
  PlanExecutionCard,
  CoverageAnalysisCard,
  NotificationChannelMessage,
  BuilderStepMessage,
  MessageBubble,
  DetailDrawer,
  ExecutionLockedModal,
} from './SubComponents';
import { UndoToast } from './UndoToast';

// ═══════════════════════════════════════════════════════════════
// MAIN COMMAND COMPONENT — Conversation-first platform assistant
// ═══════════════════════════════════════════════════════════════

const Command = ({ isOpen, onClose, splitPane = false }) => {
  const {
    agents,
    agentCount,
    workers,
    workerCount,
    loading,
    error,
    refreshAgents,
    requestRunAgent,
    activeOrganizationName,
    noOrgMessage,
    editingAgent,
    clearEditingAgent,
    setEditingAgent,
    closeCommand,
    notifyAgentModified,
    workflowContext,
    setWorkflowContext,
  } = useCommand();

  const { userPlan } = usePlan();
  const { role: userRole, isAdmin: isAdminUser } = useUserRole();
  const routerNavigate = useNavigate();
  const isMemberUser = userRole === 'member' || userRole === 'employee';
  const isBasicTier = !userPlan || userPlan === 'starter' || userPlan === 'basic';

  // ── Conversation state ──
  const [messages, setMessages] = useState([]);
  const [commandInput, setCommandInput] = useState('');
  const [commandBarListening, setCommandBarListening] = useState(false);

  // ── Undo Toast state (sticky 30s revert window after applied modifications) ──
  const [undoToast, setUndoToast] = useState(null); // { agent_id, summary } | null

  // ── Voice Mode (OpenAI TTS via backend) ──
  const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);
  const voiceModeRef = useRef(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);
  const ttsRef = useRef({
    isMuted: false,
    isSpeaking: false,
    speak: async () => {},
    stop: () => {},
    toggleMute: () => {},
  });

  // TTS speak via backend API
  const speakViaAPI = useCallback(async (text) => {
    if (!text?.trim() || isMuted) return;
    try {
      setIsSpeaking(true);
      const API = BACKEND_URL;
      const res = await fetch(`${API}/api/command/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: text.slice(0, 4096), voice: 'nova' }),
      });
      const data = await res.json();
      if (data.audio) {
        // Stop any existing playback
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audioRef.current = audio;
        audio.onended = () => { setIsSpeaking(false); audioRef.current = null; };
        audio.onerror = () => { setIsSpeaking(false); audioRef.current = null; };
        await audio.play();
      } else {
        setIsSpeaking(false);
      }
    } catch (e) {
      console.error('TTS failed:', e);
      setIsSpeaking(false);
    }
  }, [isMuted]);

  const stopTTS = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      if (!prev) stopTTS(); // Muting also stops current playback
      return !prev;
    });
  }, [stopTTS]);

  // Keep ttsRef in sync for SSE callbacks
  useEffect(() => {
    ttsRef.current = { isMuted, isSpeaking, speak: speakViaAPI, stop: stopTTS, toggleMute };
  }, [isMuted, isSpeaking, speakViaAPI, stopTTS, toggleMute]);
  useEffect(() => { voiceModeRef.current = voiceModeEnabled; }, [voiceModeEnabled]);
  const [initialized, setInitialized] = useState(false);

  // ── Stateful context (local + sent to LLM) ──
  const [lastResults, setLastResults] = useState([]);
  const sessionIdRef = useRef(`cmd-${Date.now()}`);
  const orgId = agents?.[0]?.organization_id || '';

  // ── Detail drawer state ──
  const [drawerAgent, setDrawerAgent] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Preserved state from original ──
  const [showExecutionLocked, setShowExecutionLocked] = useState(false);
  const [lastRunInfo, setLastRunInfo] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentList, setShowAgentList] = useState(false);

  const composerRef = useRef(null);
  const threadEndRef = useRef(null);
  const threadRef = useRef(null);
  const pendingOrchestrationRef = useRef(null);

  // Guard against duplicate auto-navigation (auto-dispatch + manual chip click)
  const autoNavPendingRef = useRef(false);

  // Stable bound resource — survives clarification turns, approval steps, and patch regeneration
  const [boundResource, setBoundResource] = useState(null);

  // ── Auto-open on login preference ──
  const [autoOpenOnLogin, setAutoOpenOnLogin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef(null);

  // ── Federation Wizard state ──
  const [showFederationWizard, setShowFederationWizard] = useState(false);
  const [federationWizardInitial, setFederationWizardInitial] = useState(null);

  // ── Conversation Thread state ──
  const [showThreads, setShowThreads] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const conversationIdRef = useRef(null);

  // ── Live Event Intelligence ──
  const [liveEventsMuted, setLiveEventsMuted] = useState(() => {
    try { return localStorage.getItem('command_mute_events') === 'true'; } catch { return false; }
  });
  const liveEventsMutedRef = useRef(liveEventsMuted);
  liveEventsMutedRef.current = liveEventsMuted;
  const wsRef = useRef(null);

  const toggleMuteEvents = useCallback(() => {
    setLiveEventsMuted(prev => {
      const next = !prev;
      try { localStorage.setItem('command_mute_events', String(next)); } catch {}
      return next;
    });
  }, []);

  // Format and inject a WS event as a system message in the conversation
  const injectLiveEvent = useCallback((event) => {
    if (liveEventsMutedRef.current) return;

    const EVENT_CONFIG = {
      'execution_failed': {
        icon: 'warning',
        severity: 'high',
        format: (e) => ({
          text: `Execution failed: **${e.agent_name || e.agent_id || 'Unknown agent'}**${e.error ? ` — ${e.error}` : ''}`,
          actions: [
            { type: 'command', label: 'View failures', payload: { command: 'recent failures' }, style: 'primary' },
            { type: 'command', label: 'System status', payload: { command: 'system status' }, style: 'secondary' },
          ],
        }),
      },
      'external_agent_degraded': {
        icon: 'heartbeat',
        severity: 'medium',
        format: (e) => ({
          text: `External agent **${e.agent_name || e.federation_id}** is degraded (${e.consecutive_failures || '?'} consecutive failures)`,
          actions: [
            { type: 'command', label: 'Check health', payload: { command: 'check all agent health' }, style: 'primary' },
            { type: 'command', label: 'View agents', payload: { command: 'show degraded agents' }, style: 'secondary' },
          ],
        }),
      },
      'external_agent_unreachable': {
        icon: 'warning',
        severity: 'high',
        format: (e) => ({
          text: `External agent **${e.agent_name || e.federation_id}** is unreachable${e.consecutive_failures >= 3 ? ' (auto-suspended)' : ''}`,
          actions: [
            { type: 'command', label: 'Check health', payload: { command: 'check all agent health' }, style: 'primary' },
          ],
        }),
      },
      'approval_created': {
        icon: 'shield',
        severity: e => e.risk_level === 'high' ? 'high' : 'low',
        format: (e) => ({
          text: `New approval request: ${e.target_summary || e.approval_type || 'system action'}${e.risk_level === 'high' ? ' (high risk)' : ''}`,
          actions: [
            { type: 'command', label: 'View approvals', payload: { command: 'show pending approvals' }, style: 'primary' },
          ],
        }),
      },
      'anomaly_detected': {
        icon: 'warning',
        severity: 'high',
        format: (e) => ({
          text: `Anomaly detected: **${e.title || e.description || 'System anomaly'}**`,
          actions: [
            { type: 'command', label: 'What needs attention?', payload: { command: 'what needs attention' }, style: 'primary' },
          ],
        }),
      },
    };

    const config = EVENT_CONFIG[event.type];
    if (!config) return; // Unknown event type — skip

    // Filter: only surface high/medium severity events for approvals
    if (event.type === 'approval_created') {
      const sev = typeof config.severity === 'function' ? config.severity(event) : config.severity;
      if (sev === 'low') return; // Skip low-risk approval noise
    }

    const formatted = config.format(event);
    const severity = typeof config.severity === 'function' ? config.severity(event) : config.severity;

    const systemMsg = {
      id: `live-${event.type}-${Date.now()}`,
      type: MSG.SYSTEM,
      content: formatted.text,
      actions: formatted.actions,
      liveEvent: true,
      severity,
      timestamp: event.timestamp || new Date().toISOString(),
    };

    setMessages(prev => [...prev, systemMsg]);
  }, []);

  // WebSocket connection for live events
  useEffect(() => {
    if (!orgId) return;

    const wsUrl = BACKEND_URL?.replace(/^http/, 'ws');
    if (!wsUrl) return;

    let ws;
    let reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(`${wsUrl}/api/ws/approvals?org_id=${orgId}`);
        wsRef.current = ws;

        ws.onmessage = (evt) => {
          try {
            const event = JSON.parse(evt.data);
            if (event.type === 'ping') return;
            injectLiveEvent(event);
          } catch {}
        };

        ws.onclose = () => {
          wsRef.current = null;
          reconnectTimer = setTimeout(connect, 15000);
        };

        ws.onerror = () => ws.close();
      } catch {}
    };

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [orgId, injectLiveEvent]);

  // Load a conversation thread
  const loadThread = useCallback(async (conversationId) => {
    const apiUrl = BACKEND_URL;
    if (!apiUrl) return;
    // Stop TTS when navigating to a different thread
    ttsRef.current?.stop();
    try {
      const res = await fetch(`${apiUrl}/api/command/conversation/${conversationId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const conv = await res.json();
        const loadedMessages = (conv.messages || []).map((m, i) => ({
          id: `loaded-${i}-${Date.now()}`,
          type: m.role === 'user' ? MSG.USER : MSG.ASSISTANT,
          content: m.content || '',
          timestamp: m.timestamp,
        }));
        setMessages(loadedMessages);
        conversationIdRef.current = conversationId;
        setActiveConversationId(conversationId);
        setShowThreads(false);
      }
    } catch (e) {
      console.error('Failed to load thread:', e);
    }
  }, []);

  // Start a new thread
  const startNewThread = useCallback(() => {
    setMessages([]);
    conversationIdRef.current = null;
    setActiveConversationId(null);
    setShowThreads(false);
    autoNavPendingRef.current = false;
  }, []);

  const API = BACKEND_URL;

  // Fetch auto-open preference
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const resp = await fetch(`${API}/api/account/preferences`, { credentials: 'include' });
        if (resp.ok) {
          const prefs = await resp.json();
          setAutoOpenOnLogin(!!prefs.command_default_view);
        }
      } catch { /* ignore */ }
    })();
  }, [isOpen, API]);

  const toggleAutoOpen = async () => {
    const next = !autoOpenOnLogin;
    setAutoOpenOnLogin(next);
    try {
      await fetch(`${API}/api/account/preferences`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command_default_view: next }),
      });
    } catch { /* ignore */ }
  };

  // Close settings dropdown on outside click
  useEffect(() => {
    if (!showSettings) return;
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettings(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSettings]);

  // ── Voice agents present? ──
  const hasVoiceAgents = useMemo(() =>
    agents.some(a => a.capability_profile === 'live_voice_s2s_agent' || a.voice_enabled),
  [agents]);

  // ── Connected apps state (for connect coins) ──
  const [connectedApps, setConnectedApps] = useState(new Set());
  const [connectingApp, setConnectingApp] = useState(null);

  useEffect(() => {
    if (!isOpen || !orgId) return;
    (async () => {
      try {
        const data = await checkConnectionStatus(orgId);
        setConnectedApps(new Set(data.connected_apps || []));
      } catch {}
    })();
  }, [isOpen, orgId]);

  const handleConnectAppFromCoin = useCallback(async (appName) => {
    const slug = getSlug(appName);
    if (connectedApps.has(slug)) return; // Already connected
    setConnectingApp(slug);
    try {
      const callbackUrl = `${window.location.origin}/oauth/callback?connected=${slug}`;
      const data = await connectApp(appName, orgId, callbackUrl);
      if (data.redirect_url) {
        await openOAuthPopup(data.redirect_url);
        // Re-check connection status after popup closes
        await new Promise(r => setTimeout(r, 1500));
        const freshData = await checkConnectionStatus(orgId);
        setConnectedApps(new Set(freshData.connected_apps || []));
      }
    } catch {}
    finally { setConnectingApp(null); }
  }, [orgId, connectedApps]);

  // ── Auto-scroll to bottom on new message ──
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  // ── Initialize welcome messages when first opened ──
  useEffect(() => {
    if (isOpen && !initialized && !loading) {
      // Use worker count for welcome message (Service OS workers only)
      const totalCount = workerCount;
      const welcomeMsgs = buildWelcomeMessages(totalCount, activeOrganizationName, userRole);
      setMessages(welcomeMsgs);
      setInitialized(true);
    }
  }, [isOpen, initialized, loading, agentCount, workerCount, activeOrganizationName, userRole]);

  // ── Phase 4 B2: Handle editing mode entry from Builder ──
  useEffect(() => {
    if (editingAgent && isOpen) {
      // Bind the resource so it survives across turns
      setBoundResource({
        resource_type: 'agent',
        resource_id: editingAgent.agent_id,
        resource_name: editingAgent.agent_name,
      });
      const editMsg = createMessage(MSG.SYSTEM,
        `Editing: ${editingAgent.agent_name}. Describe changes — "Add a validation step", "Replace the output format", etc.`
      );
      setMessages(prev => [...prev, editMsg]);
    }
  }, [editingAgent, isOpen]);


  // ── Reset on close ──
  useEffect(() => {
    if (!isOpen) {
      setDrawerOpen(false);
      setDrawerAgent(null);
    }
  }, [isOpen]);

  // ── Focus composer when opened ──
  useEffect(() => {
    if (isOpen && composerRef.current) {
      setTimeout(() => composerRef.current?.focus({ preventScroll: true }), 150);
    }
  }, [isOpen]);

  // ── Keyboard: ESC ──
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (drawerOpen) {
          setDrawerOpen(false);
          setDrawerAgent(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, drawerOpen, onClose]);

  // ── Helpers: push messages ──
  const pushMessage = useCallback((msg) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  // ── Handle upgrade ──
  const handleUpgrade = useCallback(() => {
    setShowExecutionLocked(false);
    routerNavigate('/settings/billing');
  }, [routerNavigate]);

  // ── Handle "Run" action ──
  const handleRunAgent = useCallback((agent) => {
    if (isBasicTier) {
      setShowExecutionLocked(true);
      return;
    }
    setDrawerOpen(false);
    setDrawerAgent(null);
    requestRunAgent(agent);
  }, [isBasicTier, requestRunAgent]);

  // ── Handle "View Details" action ──
  const handleViewDetails = useCallback((agent) => {
    setDrawerAgent(agent);
    setDrawerOpen(true);
  }, []);

  // ── Handle suggestion click ──
  const handleSuggestionRef = useRef(null);
  handleSuggestionRef.current = (value) => {
    setCommandInput('');
    streamCommandMessage(value);
  };
  const handleSuggestion = useCallback((value) => {
    handleSuggestionRef.current?.(value);
  }, []);

  // ── Track action click for telemetry ──
  const trackActionClick = useCallback((action) => {
    try {
      fetch(`${BACKEND_URL}/api/command/action-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          label: action.label || '',
          type: action.type || '',
        }),
      }).catch(() => {});
    } catch {}
  }, [BACKEND_URL]);

  // ── Handle action chip click (structured action system) ──
  const handleActionChipRef = useRef(null);
  handleActionChipRef.current = async (action) => {
    const { type, payload, target_id, parameters } = action;

    if (type === 'run_agent_inline' && payload?.agent_id) {
      const agent = agents.find(a => (a.agent_id || a.id) === payload.agent_id);
      if (agent) {
        // Prompt for input text
        const inputPrompt = `What would you like to send to ${payload.agent_name || 'this agent'}?`;
        pushMessage(createMessage(MSG.ASSISTANT, inputPrompt));
        setSelectedAgent(agent);
      }
    } else if (type === 'run_agent_modal' && payload?.agent_id) {
      const agent = agents.find(a => (a.agent_id || a.id) === payload.agent_id);
      if (agent) handleRunAgent(agent);
    } else if (type === 'open_agent' && payload?.agent_id) {
      const agent = agents.find(a => (a.agent_id || a.id) === payload.agent_id);
      if (agent) handleViewDetails(agent);
    } else if (type === 'command' && payload?.value) {
      streamCommandMessage(payload.value);
    } else if (type === 'retry' && payload?.agent_id) {
      streamCommandMessage(`run ${payload.agent_name || 'agent'} again`);
    } else if (type === 'show_runs' && payload?.agent_id) {
      streamCommandMessage(`show recent runs for ${payload.agent_name || 'agent'}`);
    } else if (type === 'show_full_output' && payload?.run_id) {
      pushMessage(createMessage(MSG.SYSTEM, 'Full output view coming soon.'));
    } else if (type === 'orchestrate' && payload?.steps) {
      // Orchestration: user clicked "Run Pipeline"
      if (payload.needs_input) {
        // Ask for initial input before starting
        const stepNames = payload.steps.map(s => s.agent_name).join(' → ');
        pushMessage(createMessage(MSG.ASSISTANT, `Pipeline ready: ${stepNames}\n\nWhat input should I start with?`, {
          actions: [],
          orchestrationPayload: payload,
        }));
        setSelectedAgent(null);
        // Store pending orchestration in a ref so submit handler can pick it up
        pendingOrchestrationRef.current = payload;
      } else {
        streamOrchestration(payload.steps, payload.initial_input || '');
      }
    } else if (type === 'retry_orchestration' && payload) {
      if (payload.orchestration_id) {
        streamOrchestration([], '', payload.orchestration_id, payload.from_step || 0);
      } else if (payload.steps) {
        streamOrchestration(payload.steps, payload.initial_input || '', null, payload.from_step || 0);
      }
    } else if (type === 'create_agent' && payload?.description) {
      // Trigger agent creation via Command
      pushMessage(createMessage(MSG.USER, 'Creating agent...'));
      streamCreation(payload.description);
    } else if (type === 'build_team' && payload?.workers?.length) {
      // Multi-worker AI Team build:
      //   1. Sequentially create each worker via /api/command/create-agent
      //   2. Once all worker IDs are collected, POST /api/systems with worker_ids[]
      //      + supervisor_instructions
      pushMessage(createMessage(MSG.USER, `Building team "${payload.team_name}"...`));
      const teamMsgId = nextId();
      pushMessage({ id: teamMsgId, type: MSG.SYSTEM, content: `Creating ${payload.workers.length} workers...`, timestamp: Date.now() });
      try {
        const apiUrl = BACKEND_URL;
        const createdIds = [];
        for (let i = 0; i < payload.workers.length; i += 1) {
          const w = payload.workers[i];
          setMessages(prev => prev.map(m => m.id === teamMsgId
            ? { ...m, content: `Creating worker ${i + 1}/${payload.workers.length}: ${w.agent_name || w.role}...` }
            : m
          ));
          const resp = await fetch(`${apiUrl}/api/command/create-agent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              description: w.description || w.agent_description || w.role,
              agent_name: w.agent_name,
            }),
          });
          if (!resp.ok) throw new Error(`Failed to create worker ${i + 1}`);
          // /api/command/create-agent is SSE; read until we find creation_saved with agent_id
          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buf = '';
          let agentId = null;
          while (!agentId) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() || '';
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'creation_saved' && evt.agent_id) {
                  agentId = evt.agent_id;
                  break;
                }
                if (evt.type === 'creation_error') {
                  throw new Error(evt.error || 'Creation error');
                }
              } catch (_e) { /* ignore parse */ }
            }
          }
          if (!agentId) throw new Error(`Worker ${i + 1} did not return an agent_id`);
          createdIds.push(agentId);
        }

        // Now create the system with all worker_ids
        setMessages(prev => prev.map(m => m.id === teamMsgId
          ? { ...m, content: `Linking ${createdIds.length} workers under supervisor...` }
          : m
        ));
        const systemResp = await fetch(`${apiUrl}/api/systems`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: payload.team_name,
            description: payload.team_description || '',
            worker_ids: createdIds,
            supervisor_instructions: payload.supervisor_instructions || '',
          }),
        });
        if (!systemResp.ok) {
          const e = await systemResp.json().catch(() => ({}));
          throw new Error(e.detail || 'Failed to create AI Team');
        }
        const systemData = await systemResp.json();
        const systemId = systemData?.system?.system_id || systemData?.system_id || systemData?.id;
        setMessages(prev => prev.map(m => m.id === teamMsgId
          ? { ...m, content: `✓ Team "${payload.team_name}" built — ${createdIds.length} workers + supervisor.${systemId ? ` Open: /systems/${systemId}` : ''}` }
          : m
        ));
        if (systemId) {
          pushMessage(createMessage(MSG.SYSTEM, `Opening team in 2 seconds...`));
          setTimeout(() => {
            closeCommand();
            routerNavigate(`/systems/${systemId}`);
          }, 2000);
        }
      } catch (err) {
        setMessages(prev => prev.map(m => m.id === teamMsgId
          ? { ...m, content: `❌ Team build failed: ${err.message}` }
          : m
        ));
      }
    } else if (type === 'cancel') {
      pushMessage(createMessage(MSG.SYSTEM, 'Cancelled.'));
    } else if (type === 'modify_agent' && payload?.agent_id) {
      // Trigger agent modification via Command — uses canonical agent_id from backend validation
      setBoundResource({
        resource_type: 'agent',
        resource_id: payload.agent_id,
        resource_name: payload.agent_name || '',
      });
      pushMessage(createMessage(MSG.USER, `Applying changes to ${payload.agent_name || 'agent'}...`));
      streamModifyAgent(payload.agent_id, payload.description || '', payload.agent_name || '');
    } else if (type === 'open_builder' && payload?.agent_id) {
      // Navigate to visual flow builder with command context
      const highlightParam = payload.highlight_nodes?.length ? `&highlight=${payload.highlight_nodes.join(',')}` : '';
      closeCommand();
      routerNavigate(`/unified-agent-builder/${payload.agent_id}?from=command${highlightParam}`);
    } else if (type === 'confirm_modify' && payload?.diff_id) {
      // Confirm a risky modification
      const confirmMsgId = nextId();
      pushMessage({ id: confirmMsgId, type: MSG.MODIFICATION, content: '', modPhase: 'analyzing', modData: {}, modError: null, timestamp: Date.now() });
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/confirm-modify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ diff_id: payload.diff_id }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        setMessages(prev => prev.map(m => m.id === confirmMsgId
          ? { ...m, modPhase: 'done', modData: { ...data, plan_summary: 'Change applied successfully.' } }
          : m
        ));
        notifyAgentModified();
      } catch (err) {
        setMessages(prev => prev.map(m => m.id === confirmMsgId ? { ...m, modPhase: 'error', modError: err.message } : m));
      }
    } else if (type === 'cancel_modify') {
      pushMessage(createMessage(MSG.SYSTEM, 'Modification cancelled.'));
    } else if (type === 'undo_modify' && payload?.agent_id) {
      // Undo last modification
      const undoMsgId = nextId();
      pushMessage({ id: undoMsgId, type: MSG.MODIFICATION, content: '', modPhase: 'analyzing', modData: {}, modError: null, timestamp: Date.now() });
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/undo-modify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ agent_id: payload.agent_id }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        setMessages(prev => prev.map(m => m.id === undoMsgId
          ? { ...m, modPhase: 'undone', modData: { message: data.message, agent_name: data.agent_name } }
          : m
        ));
        notifyAgentModified();
      } catch (err) {
        setMessages(prev => prev.map(m => m.id === undoMsgId ? { ...m, modPhase: 'error', modError: err.message } : m));
      }
    } else if (type === 'navigate' && (payload?.route || action.target)) {
      // Platform navigation — close Command and navigate
      const route = action.target || payload?.route;
      // Guard against duplicate execution if auto-dispatch already fired
      if (autoNavPendingRef.current) return;
      autoNavPendingRef.current = true;
      // Track action click
      trackActionClick(action);
      closeCommand();
      routerNavigate(route);
    } else if (type === 'execute' && (action.intent || payload?.value)) {
      // Execute action — trigger a command intent
      const cmd = action.intent || payload?.value;
      trackActionClick(action);
      streamCommandMessage(cmd);
    } else if (type === 'suggest' && (action.intent || payload?.value)) {
      // Suggest action — pre-fill Command input without auto-submitting
      const query = action.intent || payload?.value;
      trackActionClick(action);
      setCommandInput(query);
      composerRef.current?.focus({ preventScroll: true });
    } else if (type === 'launch_wizard' && payload?.wizard === 'federation_onboarding') {
      // Launch federation onboarding wizard
      setShowFederationWizard(true);
      setFederationWizardInitial(null);
    } else if (type === 'agent_manage_confirm' && payload?.action && payload?.agent_name) {
      // Agent management action (disable, enable, delete) — requires confirmation
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/agent-manage`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ action: payload.action, agent_name: payload.agent_name, context: { org_id: orgId } }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        pushMessage(createMessage(MSG.ASSISTANT, data.message));
        refreshAgents();
      } catch (err) {
        pushMessage(createMessage(MSG.ASSISTANT, `Failed: ${err.message}`));
      }
    } else if (type === 'gov_create_confirm' && payload?.description) {
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/governance/create-policy`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ description: payload.description, org_id: payload.org_id || orgId, user_id: payload.user_id }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        pushMessage(createMessage(MSG.CONFIRMATION, data.message));
      } catch (err) {
        pushMessage(createMessage(MSG.WARNING, `Policy creation failed: ${err.message}`));
      }
    } else if (type === 'gov_block_type_confirm' && payload?.provider_type) {
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/governance/block-provider-type`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ provider_type: payload.provider_type, org_id: payload.org_id || orgId, user_id: payload.user_id }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        pushMessage(createMessage(MSG.CONFIRMATION, data.message));
      } catch (err) {
        pushMessage(createMessage(MSG.WARNING, `Failed to block provider type: ${err.message}`));
      }
    } else if (type === 'gov_trust_floor_confirm' && payload?.trust_class) {
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/governance/set-trust-floor`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ trust_class: payload.trust_class, org_id: payload.org_id || orgId, user_id: payload.user_id }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        pushMessage(createMessage(MSG.CONFIRMATION, data.message));
      } catch (err) {
        pushMessage(createMessage(MSG.WARNING, `Failed to set trust floor: ${err.message}`));
      }
    } else if (type === 'gov_toggle_confirm' && payload?.policy_name) {
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/governance/toggle-policy`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ policy_name: payload.policy_name, status: payload.status, org_id: payload.org_id || orgId }),
        });
        if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${resp.status}`); }
        const data = await resp.json();
        pushMessage(createMessage(MSG.CONFIRMATION, data.message));
      } catch (err) {
        pushMessage(createMessage(MSG.WARNING, `Failed to toggle policy: ${err.message}`));
      }
    } else if (type === 'execute_plan') {
      // Plan Execution Engine — execute plan via SSE
      streamPlanExecution(payload);
    } else if (type === 'skip_step' && payload?.plan_id != null) {
      // Skip a single plan step
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/plan/${payload.plan_id}/step/${payload.step_index}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ plan_id: payload.plan_id, step_index: payload.step_index, action: 'skip' }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        pushMessage(createMessage(MSG.SYSTEM, `Skipped step: ${data.capability || 'step'}`));
        // Reload plan state
        streamPlanExecution({ plan_id: payload.plan_id, mode: 'step', start_from_step: (payload.step_index || 0) + 1 });
      } catch (err) {
        pushMessage(createMessage(MSG.WARNING, `Failed to skip step: ${err.message}`));
      }
    } else if (type === 'approve_step' && payload?.plan_id != null) {
      // Approve a plan step that needs approval
      try {
        const apiUrl = BACKEND_URL;
        const resp = await fetch(`${apiUrl}/api/command/plan/${payload.plan_id}/step/${payload.step_index}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ plan_id: payload.plan_id, step_index: payload.step_index, action: 'approve', confirmation: true }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (data.result?.success) {
          pushMessage(createMessage(MSG.CONFIRMATION, data.result.message));
        } else {
          pushMessage(createMessage(MSG.WARNING, data.result?.message || 'Approval failed'));
        }
        // Reload plan state
        streamPlanExecution({ plan_id: payload.plan_id, mode: 'step', start_from_step: (payload.step_index || 0) + 1 });
      } catch (err) {
        pushMessage(createMessage(MSG.WARNING, `Failed to approve step: ${err.message}`));
      }
    } else if (type === 'run_agent' && payload?.agent_name) {
      // Run agent — feeds into the existing Command pipeline
      streamCommandMessage(`run ${payload.agent_name}`);
    } else if (type === 'agent_manage_confirm') {
      // Agent enable/disable/delete
      const agentAction = payload?.action || parameters?.action;
      const agentName = payload?.agent_name || parameters?.agent_name;
      if (agentAction && agentName) {
        streamCommandMessage(`${agentAction} agent ${agentName}`);
      }
    } else if (type === 'app_install_confirm' && (target_id || payload?.app_name)) {
      // App install via Command
      streamCommandMessage(`install app ${payload?.app_name || target_id}`);
    } else if (type === 'app_manage_confirm') {
      const appAction = payload?.action || parameters?.action;
      const appName = payload?.app_name || parameters?.app_name;
      if (appAction && appName) {
        streamCommandMessage(`${appAction} app ${appName}`);
      }
    } else if (type === 'workflow_run_confirm' && (target_id || payload?.workflow_name)) {
      // Workflow run confirmation
      pushMessage(createMessage(MSG.CONFIRMATION, `Workflow run requested for ${payload?.workflow_name || target_id}. Routing through orchestration engine.`));
    } else if (type === 'workflow_manage_confirm') {
      const wfAction = payload?.action || parameters?.action;
      const wfName = payload?.workflow_name || parameters?.workflow_name;
      if (wfAction && wfName) {
        streamCommandMessage(`${wfAction} workflow ${wfName}`);
      }
    } else if (type === 'launch_wizard' && payload?.wizard) {
      // Launch guided wizard
      streamCommandMessage(`register an external agent`);
    }
  };
  const handleActionChip = useCallback((action) => {
    handleActionChipRef.current?.(action);
  }, []);

  // ── SSE Stream: Command messages ──
  const abortControllerRef = useRef(null);
  const streamingMsgIdRef = useRef(null);

  const streamCommandMessage = useCallback(async (inputText) => {
    if (!inputText?.trim()) return;
    const text = inputText.trim();

    // Stop any current TTS when new command starts
    ttsRef.current?.stop();

    // If a pending orchestration exists and user is providing initial input, execute the pipeline
    if (pendingOrchestrationRef.current) {
      const orch = pendingOrchestrationRef.current;
      pendingOrchestrationRef.current = null;
      pushMessage(createMessage(MSG.USER, text));
      streamOrchestration(orch.steps, text);
      return;
    }

    // Phase 4 B2: Clone/template detection — "build like X but with Y", "clone X", "similar to X"
    const clonePattern = /\b(like|similar to|based on|clone|copy|duplicate|template from|fork)\b.*\b(agent|workflow|automation|bot)?\b/i;
    const clonePattern2 = /\b(clone|copy|duplicate|fork)\s+(the\s+)?(agent\s+|workflow\s+)?(.+)/i;
    const likeButPattern = /\b(build|create|make|give)\s+(me\s+)?(an?\s+)?(agent|workflow)?\s*(like|similar to|based on)\s+(.+?)(\s+but\s+|\s+with\s+|\s+and\s+|\s+replacing\s+|\s+without\s+)/i;
    
    const isCloneIntent = likeButPattern.test(text) || clonePattern2.test(text) || 
      (clonePattern.test(text) && /\b(but|with|replacing|without|add|remove|change)\b/i.test(text));

    if (isCloneIntent && !editingAgent?.agent_id) {
      pushMessage(createMessage(MSG.USER, text));
      streamCloneAgent(text);
      return;
    }

    // Phase A: Command ↔ Builder real-time binding
    // When workflowContext is active (user is in the Builder), route step modifications
    // through the builder-step endpoint for real-time pipeline updates
    const builderStepPattern = /\b(add|insert|remove|delete|change|update|replace|move|reorder|rename)\s+(a\s+|an\s+|the\s+)?(step|trigger|action|condition|output|input|processing)/i;
    const builderAddPattern = /\b(add|insert)\s+(a\s+|an?\s+)?(new\s+)?(step|stage|task)\b/i;
    const builderContentPattern = /\b(add|insert|include|put|create)\s+(a\s+|an?\s+)?.{3,}(step|stage|after|before|between|pipeline|workflow|output|input)/i;
    const generalBuilderPattern = /\b(add|remove|delete|insert|include)\s+.{3,}/i;
    // Broader patterns for natural language workflow modifications
    const appMentionPattern = /\b(gmail|google|slack|hubspot|salesforce|sheets|calendar|xero|quickbooks|stripe|mailchimp|zapier|outlook|whatsapp|facebook|instagram|zoom|calendly|pipedrive|crm|sms|email|notify|notification|send|upload|save|store|log|create|check|verify|connect|integrate)\b/i;
    const canWePattern = /\b(can we|could we|let'?s|i want|i need|we should|we need|also|then|after that|before that|next|what if we|how about|would it be|is it possible)\b/i;

    // Phase B: Skill / Feedback intent detection
    // Behavioral instructions that shape HOW the worker executes (not WHAT steps it runs)
    // SKILL DETECTION (behavioral feedback, not structural changes)
    // These patterns aim to catch "be more polite", "always include the price",
    // etc. — NOT structural intents like "monitor emails and generate a reply".
    const skillFeedbackPattern = /\b(always|never|remember|ensure|make sure|don't forget|keep in mind|note that|important)\b/i;
    const skillTonePattern = /\b(be more|be less|sound more|sound less|tone|formal|casual|friendly|professional|polite|concise|detailed)\b/i;
    // NOTE: "need(s) to" removed — too generic ("we need to monitor emails" is structural).
    const skillRulePattern = /\b(rule|policy|approval required|must (always|never)|should always|should never|over \$|under \$|more than|less than)\b/i;
    const skillWhenPattern = /^when\s+(you|replying|responding|handling|dealing|talking|writing|sending|answering)/i;
    const skillTeachPattern = /\b(learn|teach|train|know that|understand that|aware that)\b/i;
    // Structural verbs override — if the message describes a pipeline action
    // (monitor / generate / scan / classify / send / save / fetch / extract /
    // summarize / route / forward / notify / lookup / look up), it's a STEP
    // intent regardless of any incidental skill-keyword overlap.
    const structuralVerbPattern = /\b(monitor|generate|scan|classify|categori[sz]e|send|save|store|fetch|extract|summari[sz]e|translate|route|forward|notify|alert|connect|lookup|look up|search|filter|score|validate|enrich|export|import|sync|trigger|schedule|create|delete|update|insert|append|tag|reply|respond|approve|escalate|flag|upload|download|transcribe|analyze|analyse)\b/i;

    if (workflowContext) {
      // When the AutomationBuilder is open, the visible Process Flow is the
      // single source of truth. ALL non-question messages route to builder-step
      // so changes always land where the user sees them. This eliminates the
      // class of bug where Command modifies a different worker than the one
      // visible on the right.
      const isGeneralQuestion = /^(show|help|find|explain|list|what is|how does|why does|who|where is|when did|debug|back|exit|clear|status|check)\b/i.test(text);

      // Skill feedback (behavioral) takes priority — but only if the message
      // doesn't ALSO contain structural verbs. "We need to monitor X and
      // generate Y" mentions "need" but is clearly a step intent.
      const isSkillIntent = (skillFeedbackPattern.test(text) || skillTonePattern.test(text) || skillRulePattern.test(text) || skillWhenPattern.test(text) || skillTeachPattern.test(text))
        && !structuralVerbPattern.test(text);

      if (isSkillIntent && !isGeneralQuestion) {
        pushMessage(createMessage(MSG.USER, text));
        streamBuilderSkill(text);
        return;
      }

      // Anything else that isn't a meta question goes through builder-step.
      // Old behaviour gated on builderStepPattern/appMentionPattern — that
      // missed messages like "I want this worker to monitor my emails and
      // generate replies", which are clear configuration intents but didn't
      // match an explicit verb. Now: in builder context, default = configure.
      if (!isGeneralQuestion) {
        pushMessage(createMessage(MSG.USER, text));
        streamBuilderStep(text);
        return;
      }
    }

    // Phase 4 B3: If editing an agent, route to modify endpoint
    // BUT: if the user is clearly asking to CREATE a new agent, exit editing mode first
    const createPattern = /^(build|create|make|design|set up|setup|i need|i want)\s+(me\s+)?(a|an|new)\s+(agent|workflow|automation|bot)/i;
    if (editingAgent?.agent_id) {
      if (createPattern.test(text)) {
        // User wants to create a new agent — exit editing context
        const prevName = editingAgent.agent_name;
        clearEditingAgent();
        setBoundResource(null);
        pushMessage(createMessage(MSG.SYSTEM, `Exited editing mode for ${prevName}. Starting new agent creation.`));
        // Fall through to the normal SSE stream below
      } else {
        pushMessage(createMessage(MSG.USER, text));
        // Route by backing source so writes hit the correct collection:
        //   automation → builder-step (steps[])
        //   agent      → modify-agent (flow.nodes)
        // The AutomationBuilder's workflowContext check above takes precedence
        // when the user is on that page; this is the fallback used for the
        // "build → modify" continuation after creation_done auto-binds.
        if (editingAgent.source === 'automation') {
          streamBuilderStep(text);
        } else {
          streamModifyAgent(editingAgent.agent_id, text, editingAgent.agent_name);
        }
        return;
      }
    }

    // If we have a selected agent and user is providing input, execute inline
    // But NOT if the user is asking to create a new agent
    const isCreateIntent = createPattern.test(text);
    if (!isCreateIntent && selectedAgent && !text.match(/^(show|help|find|explain|list|what|how|why|debug|back|exit|clear)/i)) {
      const inputs = selectedAgent.accepted_inputs || ['text'];
      const isTextOnly = !inputs.some(i => i.startsWith('file_') || i === 'audio' || i === 'video');

      if (isTextOnly && !isBasicTier) {
        pushMessage(createMessage(MSG.USER, text));
        streamExecution(selectedAgent.agent_id || selectedAgent.id, text);
        return;
      }
    }

    // Add user message
    pushMessage(createMessage(MSG.USER, text));

    // Create streaming assistant message placeholder
    const streamMsgId = nextId();
    streamingMsgIdRef.current = streamMsgId;
    setMessages(prev => [...prev, {
      id: streamMsgId,
      type: MSG.ASSISTANT,
      content: '',
      streaming: true,
      thinking: true,
      actions: [],
      results: [],
      timestamp: Date.now(),
    }]);

    // Abort any previous stream
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const apiUrl = BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/command/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          message: text,
          conversation_id: conversationIdRef.current || null,
          conversation: messages.slice(-8).map(m => ({
            role: m.type === MSG.USER ? 'user' : 'assistant',
            content: m.content || '',
          })),
          context: {
            org_id: orgId || '',
            last_results: lastResults,
            selected_agent: selectedAgent ? {
              agent_id: selectedAgent.agent_id || selectedAgent.id,
              name: selectedAgent.name,
            } : null,
            editing_agent: editingAgent ? {
              agent_id: editingAgent.agent_id,
              agent_name: editingAgent.agent_name,
            } : null,
            bound_resource: boundResource || null,
            session_id: sessionIdRef.current,
            workflow_context: workflowContext ? {
              worker_name: workflowContext.workerName,
              worker_id: workflowContext.workerId,
              automation_type: workflowContext.automationType,
              steps: workflowContext.steps,
            } : null,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      let streamActions = [];
      let streamResults = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            const dataStr = line.substring(6);
            try {
              const data = JSON.parse(dataStr);

              switch (eventType) {
                case 'thinking':
                  // Already showing thinking state
                  break;

                case 'conversation_id':
                  // Track the conversation ID for thread persistence
                  if (data.conversation_id) {
                    conversationIdRef.current = data.conversation_id;
                    setActiveConversationId(data.conversation_id);
                  }
                  break;

                case 'chunk':
                  fullText += data.text;
                  setMessages(prev => prev.map(m =>
                    m.id === streamMsgId
                      ? { ...m, content: fullText, thinking: false, streaming: true }
                      : m
                  ));
                  break;

                case 'results':
                  streamResults = data;
                  setMessages(prev => prev.map(m =>
                    m.id === streamMsgId
                      ? { ...m, results: data }
                      : m
                  ));
                  break;

                case 'actions':
                  streamActions = data;
                  setMessages(prev => prev.map(m =>
                    m.id === streamMsgId
                      ? { ...m, actions: data }
                      : m
                  ));
                  break;

                case 'state':
                  if (data.selected_agent) {
                    const resolved = agents.find(a =>
                      (a.agent_id || a.id) === data.selected_agent.agent_id
                    );
                    if (resolved) setSelectedAgent(resolved);
                  }
                  if (data.last_results) {
                    setLastResults(data.last_results);
                  }
                  if (data.bound_resource) {
                    setBoundResource(data.bound_resource);
                  }
                  break;

                case 'platform_data':
                  // Store platform data on the message for specialized rendering
                  if (data.type === 'federation_wizard_launch') {
                    // Auto-launch the onboarding wizard
                    setShowFederationWizard(true);
                    setFederationWizardInitial(null);
                  } else if (data.type === 'notification_channels' || data.type === 'channel_form' || data.type === 'channel_test') {
                    setMessages(prev => prev.map(m =>
                      m.id === streamMsgId
                        ? { ...m, type: MSG.NOTIFICATION_CHANNELS, platformData: data, thinking: false }
                        : m
                    ));
                  } else if (data.type === 'capability_plan' || data.type === 'capability_coverage') {
                    setMessages(prev => prev.map(m =>
                      m.id === streamMsgId
                        ? { ...m, type: MSG.CAPABILITY_PLAN, platformData: data, thinking: false }
                        : m
                    ));
                  } else {
                    setMessages(prev => prev.map(m =>
                      m.id === streamMsgId
                        ? { ...m, platformData: data }
                        : m
                    ));
                  }
                  break;

                case 'run_explanation':
                  // Switch message type to run_explanation for rich card rendering
                  setMessages(prev => prev.map(m =>
                    m.id === streamMsgId
                      ? { ...m, type: MSG.RUN_EXPLANATION, runExplanation: data, thinking: false }
                      : m
                  ));
                  break;

                case 'orchestration':
                  if (data.steps?.length > 0) {
                    // Show orchestration plan with resolved agent info
                    const planSteps = data.steps.map((s, i) => ({
                      agent_name: s.agent_name,
                      agent_id: s.agent_id,
                      resolved: !!s.agent_id,
                    }));
                    setMessages(prev => prev.map(m =>
                      m.id === streamMsgId
                        ? { ...m, orchestration: { ...data, steps: planSteps } }
                        : m
                    ));
                  }
                  break;

                case 'profile_nudge':
                  // Inline nudge when user's request depends on missing business context
                  {
                    const nudgeKey = `profile_nudge_${data.field_label}`;
                    const dismissed = sessionStorage.getItem(nudgeKey);
                    if (!dismissed) {
                      const nudgeMsg = {
                        id: `nudge-${data.field_label}-${Date.now()}`,
                        type: 'profile_nudge',
                        content: data.message,
                        nudgeData: data,
                        timestamp: Date.now(),
                      };
                      setMessages(prev => [...prev, nudgeMsg]);
                    }
                  }
                  break;

                case 'done':
                  // Merge actions, results, and intent from done event into the message.
                  // Backend platform_result path sends actions/results only in the done event,
                  // not as separate SSE events — so we must extract them here.
                  setMessages(prev => prev.map(m => {
                    if (m.id !== streamMsgId) return m;
                    const merged = { ...m, streaming: false, thinking: false };
                    if (data.actions?.length > 0) merged.actions = data.actions;
                    if (data.results?.length > 0) merged.results = data.results;
                    if (data.intent) merged.resolvedIntent = data.intent;
                    // Mark if auto-dispatch happened so chip clicks don't double-fire
                    if (data.auto_navigate) merged.autoDispatched = true;
                    return merged;
                  }));

                  // Auto-navigate when backend flags imperative intent
                  if (data.auto_navigate && data.auto_navigate_route && !autoNavPendingRef.current) {
                    autoNavPendingRef.current = true;
                    const navLabel = data.auto_navigate_label || 'page';
                    const navRoute = data.auto_navigate_route;
                    // Show visible transition state
                    const navMsgId = nextId();
                    setMessages(prev => [...prev, {
                      id: navMsgId,
                      type: MSG.SYSTEM,
                      content: `Opening ${navLabel}...`,
                      autoNavigating: true,
                      timestamp: Date.now(),
                    }]);
                    // Navigate after brief visual feedback
                    setTimeout(() => {
                      try {
                        closeCommand();
                        routerNavigate(navRoute);
                      } catch {
                        autoNavPendingRef.current = false;
                        // Surface failure with retry
                        setMessages(prev => prev.map(m =>
                          m.id === navMsgId
                            ? {
                                ...m,
                                content: `Failed to open ${navLabel}. Please try again.`,
                                type: MSG.WARNING,
                                autoNavigating: false,
                                actions: [{ type: 'navigate', label: `Retry: Open ${navLabel}`, payload: { route: navRoute }, style: 'primary' }],
                              }
                            : m
                        ));
                      }
                    }, 600);
                  }

                  // Auto-speak voice_summary if voice mode is enabled
                  if (voiceModeRef.current && data?.voice_summary && !ttsRef.current?.isMuted) {
                    ttsRef.current?.speak(data.voice_summary);
                  }
                  break;

                case 'error':
                  setMessages(prev => prev.map(m =>
                    m.id === streamMsgId
                      ? { ...m, content: data.message || 'Something went wrong.', streaming: false, thinking: false, type: MSG.WARNING }
                      : m
                  ));
                  break;

                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages(prev => prev.map(m =>
        m.id === streamMsgId
          ? { ...m, content: 'Connection interrupted. Please try again.', streaming: false, thinking: false, type: MSG.WARNING }
          : m
      ));
    }

    streamingMsgIdRef.current = null;
  }, [agents, messages, selectedAgent, isBasicTier, lastResults, orgId, pushMessage]);

  // ── SSE Stream: Plan Execution (Capability Plan → Action) ──
  const streamPlanExecution = useCallback(async (payload) => {
    const { plan_data, plan_id, mode = 'staged', start_from_step = 0, skip_steps = [] } = payload || {};

    // Create plan execution message
    const execMsgId = nextId();
    setMessages(prev => [...prev, {
      id: execMsgId,
      type: MSG.PLAN_EXECUTION,
      content: '',
      planExecution: {
        plan_id: plan_id || null,
        goal: plan_data?.goal || '',
        steps: (plan_data?.plan_steps || []).map((s, i) => ({
          index: i,
          capability: s.capability || s.name || '',
          category: s.category || 'unknown',
          exec_status: 'pending',
          action_type: s.status === 'available' ? 'configure_orchestration' : 'create_agent',
          message: null,
        })),
        status: 'starting',
        completed: 0,
        skipped: 0,
        failed: 0,
        total: plan_data?.plan_steps?.length || 0,
      },
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const body = {};
      if (plan_id) body.plan_id = plan_id;
      if (plan_data) body.plan_data = plan_data;
      body.mode = mode;
      body.start_from_step = start_from_step;
      body.skip_steps = skip_steps;

      const resp = await fetch(`${apiUrl}/api/command/execute-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        setMessages(prev => prev.map(m => m.id === execMsgId
          ? { ...m, planExecution: { ...m.planExecution, status: 'failed' } }
          : m
        ));
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6));

              if (eventType === 'plan_init') {
                const initStatus = data.mode === 'preview' ? 'preview' : 'executing';
                setMessages(prev => prev.map(m => m.id === execMsgId ? {
                  ...m,
                  planExecution: {
                    ...m.planExecution,
                    plan_id: data.plan_id,
                    goal: data.goal,
                    status: initStatus,
                    total: data.total_steps,
                    steps: data.steps_summary?.map(s => ({
                      index: s.index,
                      capability: s.capability,
                      category: s.category,
                      exec_status: s.exec_status,
                      action_type: s.action_type,
                      description: s.description || '',
                      system: s.system || '',
                      system_route: s.system_route || '',
                      risk: s.risk || 'low',
                      risk_reason: s.risk_reason || '',
                      expected_outcome: s.expected_outcome || '',
                      requires_approval: s.requires_approval || false,
                      message: null,
                      suggested_actions: null,
                    })) || m.planExecution.steps,
                  },
                } : m));
              }

              else if (eventType === 'plan_step_start') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  const steps = [...(m.planExecution?.steps || [])];
                  const idx = data.index;
                  if (steps[idx]) steps[idx] = {
                    ...steps[idx],
                    exec_status: 'in_progress',
                    description: data.description || steps[idx].description,
                    system: data.system || steps[idx].system,
                    risk: data.risk || steps[idx].risk,
                    risk_reason: data.risk_reason || steps[idx].risk_reason,
                    expected_outcome: data.expected_outcome || steps[idx].expected_outcome,
                  };
                  return { ...m, planExecution: { ...m.planExecution, steps, status: 'executing' } };
                }));
              }

              else if (eventType === 'plan_step_progress') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  const steps = [...(m.planExecution?.steps || [])];
                  const idx = data.index;
                  if (steps[idx]) steps[idx] = { ...steps[idx], message: data.message, system: data.system || steps[idx].system };
                  return { ...m, planExecution: { ...m.planExecution, steps } };
                }));
              }

              else if (eventType === 'plan_step_complete') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  const steps = [...(m.planExecution?.steps || [])];
                  const idx = data.index;
                  if (steps[idx]) steps[idx] = { ...steps[idx], exec_status: 'complete', message: data.message };
                  const completed = steps.filter(s => s.exec_status === 'complete').length;
                  return { ...m, planExecution: { ...m.planExecution, steps, completed } };
                }));
              }

              else if (eventType === 'plan_step_skipped') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  const steps = [...(m.planExecution?.steps || [])];
                  const idx = data.index;
                  if (steps[idx]) steps[idx] = { ...steps[idx], exec_status: 'skipped', message: data.reason };
                  const skippedCount = steps.filter(s => s.exec_status === 'skipped').length;
                  return { ...m, planExecution: { ...m.planExecution, steps, skipped: skippedCount } };
                }));
              }

              else if (eventType === 'plan_step_approval') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  const steps = [...(m.planExecution?.steps || [])];
                  const idx = data.index;
                  if (steps[idx]) steps[idx] = {
                    ...steps[idx],
                    exec_status: 'approval_needed',
                    message: `Requires approval: ${data.description}`,
                    risk_reason: data.risk_reason || steps[idx].risk_reason,
                  };
                  return { ...m, planExecution: { ...m.planExecution, steps, status: 'paused' } };
                }));
              }

              else if (eventType === 'plan_step_failed') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  const steps = [...(m.planExecution?.steps || [])];
                  const idx = data.index;
                  if (steps[idx]) steps[idx] = {
                    ...steps[idx],
                    exec_status: 'failed',
                    message: data.message,
                    suggested_actions: data.suggested_actions || [],
                  };
                  const failedCount = steps.filter(s => s.exec_status === 'failed').length;
                  return { ...m, planExecution: { ...m.planExecution, steps, failed: failedCount, status: 'paused' } };
                }));
              }

              else if (eventType === 'plan_done') {
                setMessages(prev => prev.map(m => {
                  if (m.id !== execMsgId) return m;
                  return {
                    ...m,
                    planExecution: {
                      ...m.planExecution,
                      plan_id: data.plan_id,
                      status: data.status,
                      completed: data.completed,
                      skipped: data.skipped,
                      failed: data.failed,
                      total: data.total,
                    },
                  };
                }));
                // Refresh agents if we created any
                refreshAgents();
                // Auto-speak plan completion summary
                if (voiceModeRef.current && data?.voice_summary && !ttsRef.current?.isMuted) {
                  ttsRef.current?.speak(data.voice_summary);
                }
              }

              else if (eventType === 'plan_error') {
                setMessages(prev => prev.map(m => m.id === execMsgId
                  ? { ...m, planExecution: { ...m.planExecution, status: 'failed' }, content: data.message }
                  : m
                ));
              }

            } catch (parseErr) {
              // Ignore parse errors on SSE chunks
            }
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m => m.id === execMsgId
        ? { ...m, planExecution: { ...m.planExecution, status: 'failed' }, content: err.message }
        : m
      ));
    }
  }, [pushMessage]);

  // ── SSE Stream: Execution (for text-input agents) ──
  const streamExecution = useCallback(async (agentId, inputText) => {
    // Create execution message with step tracking
    const execMsgId = nextId();
    setMessages(prev => [...prev, {
      id: execMsgId,
      type: 'execution',
      content: '',
      steps: [],
      execStatus: 'starting',
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/command/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agent_id: agentId,
          input_text: inputText,
          conversation: [],
          context: { org_id: orgId || '' },
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'exec_step':
                  setMessages(prev => prev.map(m => {
                    if (m.id !== execMsgId) return m;
                    const steps = [...(m.steps || [])];
                    const idx = steps.findIndex(s => s.step === data.step);
                    if (idx >= 0) {
                      steps[idx] = data;
                    } else {
                      steps.push(data);
                    }
                    return { ...m, steps, execStatus: data.status === 'active' ? 'running' : m.execStatus };
                  }));
                  break;

                case 'exec_complete':
                  setMessages(prev => prev.map(m =>
                    m.id === execMsgId
                      ? { ...m, execStatus: data.status, execResult: data }
                      : m
                  ));
                  break;

                case 'exec_error':
                  setMessages(prev => prev.map(m =>
                    m.id === execMsgId
                      ? { ...m, execStatus: 'failed', execResult: data }
                      : m
                  ));
                  break;

                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === execMsgId
          ? { ...m, execStatus: 'failed', execResult: { message: 'Connection lost. Please retry.', actions: [{ type: 'retry', label: 'Retry', payload: { agent_id: agentId }, style: 'primary' }] } }
          : m
      ));
    }
  }, [orgId]);

  // ── SSE Stream: Orchestration (sequential multi-agent pipeline) ──
  const streamOrchestration = useCallback(async (steps, initialInput, orchestrationId = null, fromStep = 0) => {
    const orchMsgId = nextId();
    setMessages(prev => [...prev, {
      id: orchMsgId,
      type: MSG.ORCHESTRATION,
      content: '',
      orchSteps: [],
      orchStatus: 'starting',
      orchResult: null,
      orchPlan: null,
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const isRetry = !!orchestrationId;
      const url = isRetry ? `${apiUrl}/api/command/orchestrate/retry` : `${apiUrl}/api/command/orchestrate`;
      const payload = isRetry
        ? { orchestration_id: orchestrationId, from_step: fromStep, input_override: initialInput || undefined, context: { org_id: orgId || '' } }
        : { steps, initial_input: initialInput || '', context: { org_id: orgId || '' } };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let orchStepsState = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'orch_plan': {
                  // Initialize steps from the plan
                  const planSteps = (data.steps || []).map((s, i) => ({
                    index: i,
                    agent_name: s.agent_name,
                    agent_id: s.agent_id,
                    status: 'pending',
                    input_preview: null,
                    output_preview: null,
                    error: null,
                    duration_ms: null,
                  }));
                  orchStepsState = planSteps;
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId
                      ? { ...m, orchSteps: planSteps, orchPlan: data, orchStatus: 'running' }
                      : m
                  ));
                  break;
                }

                case 'orch_step_start': {
                  orchStepsState = orchStepsState.map(s =>
                    s.index === data.step_index
                      ? { ...s, status: 'running', input_preview: data.input_preview }
                      : s
                  );
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId ? { ...m, orchSteps: [...orchStepsState] } : m
                  ));
                  break;
                }

                case 'orch_step_progress': {
                  orchStepsState = orchStepsState.map(s =>
                    s.index === data.step_index
                      ? { ...s, message: data.message }
                      : s
                  );
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId ? { ...m, orchSteps: [...orchStepsState] } : m
                  ));
                  break;
                }

                case 'orch_step_complete': {
                  orchStepsState = orchStepsState.map(s =>
                    s.index === data.step_index
                      ? { ...s, status: 'completed', output_preview: data.output_preview, duration_ms: data.duration_ms, run_id: data.run_id, message: null }
                      : s
                  );
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId ? { ...m, orchSteps: [...orchStepsState] } : m
                  ));
                  break;
                }

                case 'orch_step_failed': {
                  orchStepsState = orchStepsState.map(s =>
                    s.index === data.step_index
                      ? { ...s, status: 'failed', error: data.error, duration_ms: data.duration_ms, message: null }
                      : s
                  );
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId
                      ? { ...m, orchSteps: [...orchStepsState], orchStatus: 'failed', orchResult: data }
                      : m
                  ));
                  break;
                }

                case 'orch_complete': {
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId
                      ? { ...m, orchStatus: 'completed', orchResult: data }
                      : m
                  ));
                  break;
                }

                case 'orch_failed': {
                  setMessages(prev => prev.map(m =>
                    m.id === orchMsgId
                      ? { ...m, orchStatus: 'failed', orchResult: { ...data, actions: data.actions || [] } }
                      : m
                  ));
                  break;
                }

                case 'done':
                  break;

                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === orchMsgId
          ? {
              ...m,
              orchStatus: 'failed',
              orchResult: {
                error: err.message || 'Connection lost during pipeline execution.',
                actions: [
                  { type: 'retry_orchestration', label: 'Retry Pipeline', payload: { steps, from_step: 0 }, style: 'primary' },
                ],
              },
            }
          : m
      ));
    }
  }, [orgId]);


  // ── SSE Stream: Agent Creation ──
  const streamCreation = useCallback(async (description) => {
    const createMsgId = nextId();
    setMessages(prev => [...prev, {
      id: createMsgId,
      type: MSG.CREATION,
      content: '',
      creationPhase: 'start',
      creationData: {},
      creationError: null,
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/command/create-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description, context: { org_id: orgId || '' } }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let creationDataAccum = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'creation_start':
                  creationDataAccum.start = data;
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'start', creationData: { ...creationDataAccum } }
                      : m
                  ));
                  break;

                case 'creation_intent':
                  creationDataAccum.intent = data;
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'intent', creationData: { ...creationDataAccum } }
                      : m
                  ));
                  break;

                case 'creation_workflow':
                  creationDataAccum.workflow = data;
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'workflow', creationData: { ...creationDataAccum } }
                      : m
                  ));
                  break;

                case 'creation_enhanced':
                  creationDataAccum.enhanced = data;
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'enhanced', creationData: { ...creationDataAccum } }
                      : m
                  ));
                  break;

                case 'creation_saved':
                  creationDataAccum.saved = data;
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'saved', creationData: { ...creationDataAccum } }
                      : m
                  ));
                  break;

                case 'creation_done':
                  creationDataAccum.done = data;
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'done', creationData: { ...creationDataAccum } }
                      : m
                  ));
                  // ── Continuation auto-bind ──
                  // After a worker is built, automatically bind subsequent messages
                  // to that worker (Lindy/Cursor-style). User can clear via the
                  // "Editing: X · clear" chip or by typing "build a new worker".
                  if (data.agent_id) {
                    setEditingAgent({
                      agent_id: data.agent_id,
                      agent_name: data.agent_name || 'New Worker',
                      source: data.source || 'agent',
                      origin: 'creation',
                    });
                  }
                  // Refresh the workers list so the new one appears in pickers
                  if (refreshAgents) refreshAgents();
                  break;

                case 'creation_error':
                  setMessages(prev => prev.map(m =>
                    m.id === createMsgId
                      ? { ...m, creationPhase: 'error', creationError: data.error, creationData: { ...creationDataAccum } }
                      : m
                  ));
                  break;

                case 'done':
                  break;

                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === createMsgId
          ? { ...m, creationPhase: 'error', creationError: err.message || 'Connection lost during agent creation.' }
          : m
      ));
    }
  }, [orgId]);


  // ── SSE Stream: Modify Agent (editing mode) ──
  const streamModifyAgent = useCallback(async (agentId, instruction, agentName = '') => {
    const modMsgId = nextId();
    setMessages(prev => [...prev, {
      id: modMsgId,
      type: MSG.MODIFICATION,
      content: '',
      modPhase: 'analyzing',
      modData: {},
      modError: null,
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/command/modify-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agent_id: agentId, instruction, context: { org_id: orgId || '', agent_name: agentName || '' } }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let modDataAccum = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'modify_start':
                  modDataAccum = { ...modDataAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === modMsgId ? { ...m, modPhase: 'analyzing', modData: { ...modDataAccum } } : m
                  ));
                  break;
                case 'modify_plan':
                  modDataAccum = { ...modDataAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === modMsgId ? { ...m, modPhase: 'plan', modData: { ...modDataAccum } } : m
                  ));
                  break;
                case 'modify_preview':
                  modDataAccum = { ...modDataAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === modMsgId ? { ...m, modPhase: 'preview', modData: { ...modDataAccum } } : m
                  ));
                  break;
                case 'modify_applied':
                  modDataAccum = { ...modDataAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === modMsgId ? { ...m, modPhase: 'applied', modData: { ...modDataAccum } } : m
                  ));
                  break;
                case 'modify_saved':
                  modDataAccum = { ...modDataAccum, ...data };
                  // Notify the Builder to re-fetch the agent config
                  notifyAgentModified();
                  break;
                case 'modify_done':
                  modDataAccum = { ...modDataAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === modMsgId ? { ...m, modPhase: 'done', modData: { ...modDataAccum } } : m
                  ));
                  // Show the floating Undo toast only when an actual change was applied
                  // (skip the no_op clarifier path).
                  if (!data.no_op && data.agent_id) {
                    const cs = data.change_summary || {};
                    const added = (cs.added || []).length;
                    const removed = (cs.removed || []).length;
                    const modified = (cs.modified || []).length;
                    const parts = [];
                    if (added) parts.push(`+${added} added`);
                    if (removed) parts.push(`−${removed} removed`);
                    if (modified) parts.push(`${modified} modified`);
                    const summary = parts.length
                      ? parts.join(', ')
                      : (data.plan_summary || 'Change applied');
                    setUndoToast({ agent_id: data.agent_id, summary });
                  }
                  break;
                case 'modify_error':
                  setMessages(prev => prev.map(m =>
                    m.id === modMsgId ? { ...m, modPhase: 'error', modError: data.error, modData: { ...modDataAccum, recovery_actions: data.recovery_actions || [] } } : m
                  ));
                  break;
                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === modMsgId
          ? { ...m, modPhase: 'error', modError: err.message || 'Connection lost during modification.' }
          : m
      ));
    }
  }, [orgId]);



  // ── Clone/Template Stream ── POST /api/command/clone-agent SSE ──

  const streamCloneAgent = useCallback(async (instruction, sourceAgentId) => {
    const cloneMsgId = nextId();
    setMessages(prev => [...prev, {
      id: cloneMsgId, type: MSG.CLONE_TEMPLATE, content: '',
      clonePhase: 'parsing', cloneData: {}, cloneError: null,
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/command/clone-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          instruction,
          source_agent_id: sourceAgentId || null,
          context: { org_id: orgId || '' },
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let cloneAccum = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'clone_parse':
                  cloneAccum = { ...cloneAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === cloneMsgId ? { ...m, clonePhase: 'parsing', cloneData: { ...cloneAccum } } : m
                  ));
                  break;
                case 'clone_source':
                  cloneAccum = { ...cloneAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === cloneMsgId ? { ...m, clonePhase: 'source_found', cloneData: { ...cloneAccum } } : m
                  ));
                  break;
                case 'clone_diff':
                  cloneAccum = { ...cloneAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === cloneMsgId ? { ...m, clonePhase: 'diffing', cloneData: { ...cloneAccum } } : m
                  ));
                  break;
                case 'clone_created':
                  cloneAccum = { ...cloneAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === cloneMsgId ? { ...m, clonePhase: 'created', cloneData: { ...cloneAccum } } : m
                  ));
                  break;
                case 'clone_done':
                  cloneAccum = { ...cloneAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === cloneMsgId ? { ...m, clonePhase: 'done', cloneData: { ...cloneAccum } } : m
                  ));
                  refreshAgents();
                  break;
                case 'clone_error':
                  cloneAccum = { ...cloneAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === cloneMsgId ? { ...m, clonePhase: 'error', cloneError: data.error, cloneData: { ...cloneAccum } } : m
                  ));
                  break;
                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === cloneMsgId
          ? { ...m, clonePhase: 'error', cloneError: err.message || 'Connection lost during cloning.' }
          : m
      ));
    }
  }, [orgId]);

  // ── Builder Steps Sync — keep latest steps from Builder ──
  const builderStepsRef = useRef(null);
  useEffect(() => {
    const handleSync = (e) => {
      const { steps, workerType, workerName, workerId, skills } = e.detail || {};
      builderStepsRef.current = { steps, workerType, workerName, workerId, skills };
    };
    window.addEventListener('builder-steps-sync', handleSync);
    return () => window.removeEventListener('builder-steps-sync', handleSync);
  }, []);

  // ── SSE Stream: Builder Step Modification (Command ↔ Builder binding) ──
  const streamBuilderStep = useCallback(async (instruction) => {
    const stepMsgId = nextId();
    setMessages(prev => [...prev, {
      id: stepMsgId,
      type: MSG.BUILDER_STEP,
      content: '',
      builderPhase: 'analyzing',
      builderData: {},
      builderError: null,
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const ctx = builderStepsRef.current || {};
      const currentSteps = ctx.steps || workflowContext?.steps || [];
      const workerType = ctx.workerType || workflowContext?.automationType || 'event';
      const workerName = ctx.workerName || workflowContext?.workerName || 'Worker';

      const response = await fetch(`${apiUrl}/api/command/builder-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          instruction,
          current_steps: currentSteps,
          worker_type: workerType,
          worker_name: workerName,
          context: { org_id: orgId || '' },
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let builderAccum = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'step_start':
                  builderAccum = { ...builderAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === stepMsgId ? { ...m, builderPhase: 'analyzing', builderData: { ...builderAccum } } : m
                  ));
                  break;

                case 'step_parsed':
                  builderAccum = { ...builderAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === stepMsgId ? { ...m, builderPhase: 'parsed', builderData: { ...builderAccum } } : m
                  ));
                  break;

                case 'step_applied':
                  builderAccum = { ...builderAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === stepMsgId ? { ...m, builderPhase: 'applied', builderData: { ...builderAccum } } : m
                  ));
                  // Dispatch event to AutomationBuilder to update steps in real-time
                  if (data.updated_steps) {
                    window.dispatchEvent(new CustomEvent('builder-step-update', {
                      detail: {
                        updatedSteps: data.updated_steps,
                        operations: data.operations || [],
                      }
                    }));
                  }
                  break;

                case 'step_done':
                  builderAccum = { ...builderAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === stepMsgId ? { ...m, builderPhase: 'done', builderData: { ...builderAccum } } : m
                  ));
                  break;

                case 'step_error':
                  setMessages(prev => prev.map(m =>
                    m.id === stepMsgId ? { ...m, builderPhase: 'error', builderError: data.error } : m
                  ));
                  break;

                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === stepMsgId
          ? { ...m, builderPhase: 'error', builderError: err.message || 'Connection lost.' }
          : m
      ));
    }
  }, [orgId, workflowContext]);

  // ── SSE Stream: Builder Skill Teaching (Phase B: Feedback & Self-Learning) ──
  const streamBuilderSkill = useCallback(async (instruction) => {
    const skillMsgId = nextId();
    setMessages(prev => [...prev, {
      id: skillMsgId,
      type: MSG.BUILDER_SKILL,
      content: '',
      skillPhase: 'analyzing',
      skillData: {},
      skillError: null,
      timestamp: Date.now(),
    }]);

    try {
      const apiUrl = BACKEND_URL;
      const ctx = builderStepsRef.current || {};
      const workerType = ctx.workerType || workflowContext?.automationType || 'event';
      const workerName = ctx.workerName || workflowContext?.workerName || 'Worker';
      const workerId = ctx.workerId || workflowContext?.workerId || '';

      // Get existing skills from builder
      const existingSkills = builderStepsRef.current?.skills || [];

      const response = await fetch(`${apiUrl}/api/command/builder-skill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          instruction,
          worker_type: workerType,
          worker_name: workerName,
          worker_id: workerId,
          existing_skills: existingSkills,
          context: { org_id: orgId || '' },
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let skillAccum = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.substring(6));

              switch (eventType) {
                case 'skill_start':
                  skillAccum = { ...skillAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === skillMsgId ? { ...m, skillPhase: 'analyzing', skillData: { ...skillAccum } } : m
                  ));
                  break;

                case 'skill_parsed':
                  skillAccum = { ...skillAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === skillMsgId ? { ...m, skillPhase: 'parsed', skillData: { ...skillAccum } } : m
                  ));
                  // Dispatch to Builder even before DB save
                  if (data.skill && data.is_valid) {
                    window.dispatchEvent(new CustomEvent('builder-skill-update', {
                      detail: { skill: data.skill, action: 'add' }
                    }));
                  }
                  break;

                case 'skill_saved':
                  skillAccum = { ...skillAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === skillMsgId ? { ...m, skillPhase: 'saved', skillData: { ...skillAccum } } : m
                  ));
                  break;

                case 'skill_done':
                  skillAccum = { ...skillAccum, ...data };
                  setMessages(prev => prev.map(m =>
                    m.id === skillMsgId ? { ...m, skillPhase: 'done', skillData: { ...skillAccum } } : m
                  ));
                  break;

                case 'skill_error':
                  setMessages(prev => prev.map(m =>
                    m.id === skillMsgId ? { ...m, skillPhase: 'error', skillError: data.error } : m
                  ));
                  break;

                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
            eventType = '';
          }
        }
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === skillMsgId
          ? { ...m, skillPhase: 'error', skillError: err.message || 'Connection lost.' }
          : m
      ));
    }
  }, [orgId, workflowContext]);


  // ── Form submit handler ──
  const handleSubmit = useCallback((e) => {
    e?.preventDefault();
    if (!commandInput.trim()) return;
    const text = commandInput.trim();
    setCommandInput('');
    // Reset textarea height
    if (composerRef.current) {
      composerRef.current.style.height = '80px';
    }
    streamCommandMessage(text);
  }, [commandInput, streamCommandMessage]);

  // ── Handle voice transcript ──
  const handleVoiceTranscript = useCallback((transcript) => {
    if (transcript?.trim()) {
      setCommandInput('');
      streamCommandMessage(transcript.trim());
    }
  }, [streamCommandMessage]);

  if (!isOpen) return null;

  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════

  const panelContent = (
    <div
      className={splitPane
        ? "relative w-full h-full bg-[var(--surface-0)] flex flex-col border-r border-[var(--border-default)]"
        : "fixed top-0 right-0 bottom-0 z-[9999] flex justify-end pointer-events-none"
      }
      role="dialog"
      aria-modal={!splitPane}
      aria-label="Command"
      data-testid="command-drawer"
    >
      {/* Dock Panel — full screen on mobile, side panel on desktop; split pane = inline left panel */}
      <div
        className={splitPane
          ? "relative w-full h-full flex flex-col overflow-hidden"
          : "relative w-full h-full bg-[var(--surface-0)] flex flex-col md:max-w-[560px] md:border-l md:border-[var(--border-default)] md:shadow-2xl pointer-events-auto"
        }
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── HEADER ── */}
        <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between shrink-0" data-testid="command-header">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[var(--brand-primary)] flex items-center justify-center">
              <Lightning size={16} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-[var(--text-primary)]">Command</h1>
              {!editingAgent && selectedAgent && (
                <p className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                  <CaretRight size={9} /> {selectedAgent.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {editingAgent && (
              <button
                onClick={() => { clearEditingAgent(); setBoundResource(null); pushMessage(createMessage(MSG.SYSTEM, 'Exited editing mode.')); }}
                className="px-2.5 py-1 rounded-lg text-xs text-[var(--text-muted)] hover:bg-[var(--surface-3)] transition-colors"
                data-testid="command-exit-editing"
              >
                Exit Editing
              </button>
            )}
            {selectedAgent && (
              <button
                onClick={() => { setSelectedAgent(null); pushMessage(createMessage(MSG.SYSTEM, 'Returned to Command.')); }}
                className="p-1.5 rounded-lg text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)] transition-colors"
                title="Back to Command"
              >
                <ArrowsClockwise size={15} />
              </button>
            )}
            {/* Settings dropdown */}
            <div className="relative" ref={settingsRef}>
              <button
                onClick={() => setShowThreads(prev => !prev)}
                className={`p-1.5 rounded-lg transition-colors ${showThreads ? 'bg-[var(--surface-3)] text-[var(--brand-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]'}`}
                title="Conversation threads"
                data-testid="command-threads-btn"
              >
                <ChatText size={15} />
              </button>
            </div>
            <div className="relative">
              <button
                onClick={toggleMuteEvents}
                className={`p-1.5 rounded-lg transition-colors ${liveEventsMuted ? 'text-[var(--text-muted)] hover:text-[var(--text-primary)]' : 'text-emerald-400'} hover:bg-[var(--surface-3)]`}
                title={liveEventsMuted ? 'Live events muted — click to unmute' : 'Live events active — click to mute'}
                data-testid="command-mute-btn"
              >
                {liveEventsMuted ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 2l20 20"/><path d="M9.9 4.24A2 2 0 0112 4a2 2 0 012 2v4c0 .28-.06.54-.16.78"/><path d="M5 10a7 7 0 0012.54 4.22"/><path d="M8.54 16.44A5 5 0 0112 18a5 5 0 005-5"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" fill="currentColor"/><path d="M12 2a10 10 0 0110 10"/><path d="M12 6a6 6 0 016 6"/></svg>
                )}
              </button>
            </div>
            {/* Voice Mode Controls */}
            {/* Voice Mode Controls (OpenAI TTS) */}
            <div className="flex items-center gap-0.5">
              {/* Voice toggle */}
              <button
                onClick={() => {
                  setVoiceModeEnabled(prev => {
                    if (prev) stopTTS();
                    return !prev;
                  });
                }}
                className={`p-1.5 rounded-lg transition-all ${
                  voiceModeEnabled
                    ? isSpeaking
                      ? 'text-[#8B5CF6] bg-[rgba(139,92,246,0.12)]'
                      : 'text-[#8B5CF6] hover:bg-[var(--surface-3)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]'
                }`}
                title={voiceModeEnabled ? 'Voice mode on — click to disable' : 'Enable voice mode'}
                data-testid="voice-mode-toggle"
              >
                {voiceModeEnabled ? (
                  <SpeakerHigh size={15} weight="fill" className={isSpeaking ? 'animate-pulse' : ''} />
                ) : (
                  <SpeakerSimpleSlash size={15} />
                )}
              </button>
              {/* Stop speaking / Mute button (visible when voice mode is on) */}
              {voiceModeEnabled && (
                <button
                  onClick={() => {
                    if (isSpeaking) {
                      stopTTS();
                    } else {
                      toggleMute();
                    }
                  }}
                  className={`p-1.5 rounded-lg transition-all ${
                    isSpeaking
                      ? 'text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)]'
                      : isMuted
                      ? 'text-[#F59E0B] hover:bg-[rgba(245,158,11,0.08)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]'
                  }`}
                  title={isSpeaking ? 'Stop speaking' : isMuted ? 'Unmute voice' : 'Mute voice'}
                  data-testid="voice-mute-btn"
                >
                  {isSpeaking ? (
                    <Stop size={14} weight="fill" />
                  ) : isMuted ? (
                    <SpeakerSimpleSlash size={14} />
                  ) : (
                    <SpeakerSimpleHigh size={14} />
                  )}
                </button>
              )}
            </div>
            <div className="relative" ref={settingsRef}>
              <button
                onClick={() => setShowSettings(prev => !prev)}
                className={`p-1.5 rounded-lg transition-colors ${showSettings ? 'bg-[var(--surface-3)] text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]'}`}
                aria-label="Command settings"
                data-testid="command-settings-btn"
              >
                <GearSix size={15} />
              </button>
              {showSettings && (
                <div className="absolute right-0 top-full mt-1 w-56 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] shadow-xl z-50 py-1.5" data-testid="command-settings-dropdown">
                  <button
                    onClick={toggleAutoOpen}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[var(--surface-3)] transition-colors"
                    data-testid="toggle-auto-open"
                  >
                    <span className="text-[var(--text-secondary)]">Open on login</span>
                    <div className={`w-8 h-[18px] rounded-full transition-colors relative ${autoOpenOnLogin ? 'bg-[var(--brand-primary)]' : 'bg-[var(--surface-3)] border border-[var(--border-default)]'}`}>
                      <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform ${autoOpenOnLogin ? 'translate-x-[16px]' : 'translate-x-[2px]'}`} />
                    </div>
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-[var(--surface-3)] transition-colors"
              aria-label="Close (ESC)"
              data-testid="command-close"
            >
              <X size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
        </div>

        {/* ── EDITING BANNER ── */}
        {editingAgent && (
          <div className="px-4 py-2.5 bg-[var(--brand-primary)]/8 border-b border-[var(--brand-primary)]/15 flex items-center justify-between shrink-0" data-testid="command-editing-banner">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-[var(--brand-primary)]/15 flex items-center justify-center">
                <Wrench size={12} className="text-[var(--brand-primary)]" />
              </div>
              <div>
                <p className="text-[11px] text-[var(--brand-primary)] font-semibold">Editing Agent</p>
                <p className="text-[13px] text-[var(--text-primary)] font-medium leading-tight">{editingAgent.agent_name}</p>
              </div>
            </div>
            <button
              onClick={() => { clearEditingAgent(); setBoundResource(null); pushMessage(createMessage(MSG.SYSTEM, 'Exited editing mode.')); }}
              className="px-2 py-1 rounded-md text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)] transition-colors uppercase tracking-wider font-medium"
              data-testid="command-editing-banner-exit"
            >
              Exit
            </button>
          </div>
        )}

        {/* ── CONVERSATION THREADS PANEL ── */}
        {showThreads ? (
          <ConversationThreads
            onSelectThread={loadThread}
            activeThreadId={activeConversationId}
            onNewThread={startNewThread}
            onClose={() => setShowThreads(false)}
          />
        ) : showFederationWizard ? (
          <FederationWizard
            initialData={federationWizardInitial}
            onClose={() => setShowFederationWizard(false)}
            onComplete={(result) => {
              setShowFederationWizard(false);
              if (result?.action === 'submitted') {
                pushMessage(createMessage(MSG.CONFIRMATION,
                  `External agent submitted for review. You can track it in My Agents.`
                ));
              } else if (result?.action === 'draft') {
                pushMessage(createMessage(MSG.SYSTEM,
                  `Draft saved. Resume from My Agents when ready.`
                ));
              }
            }}
          />
        ) : (
        <>
        {/* ── CONVERSATION THREAD ── */}
        <div className="flex-1 overflow-y-auto" ref={threadRef} data-testid="command-thread">
          <div className="py-4 px-4 space-y-4 md:pl-5 md:pr-4 md:max-w-[520px] md:ml-0">
            {/* Loading state */}
            {loading && messages.length === 0 && (
              <div className="flex items-center justify-center h-32">
                <CircleNotch size={20} className="animate-spin text-[var(--brand-primary)]" />
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-lg bg-[rgba(239,68,68,0.12)] flex items-center justify-center shrink-0">
                  <WarningCircle size={14} className="text-[#F87171]" />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-[#F87171]">{error}</p>
                  <button onClick={refreshAgents} className="text-xs text-[var(--brand-primary)] hover:underline">Retry</button>
                </div>
              </div>
            )}

            {/* No org state */}
            {noOrgMessage && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0">
                  <Info size={14} className="text-[var(--text-muted)]" />
                </div>
                <p className="text-sm text-[var(--text-secondary)]">{noOrgMessage}</p>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onAction={handleActionChip}
                onSuggestion={handleSuggestion}
                onRun={handleRunAgent}
                onViewDetails={handleViewDetails}
                isBasicTier={isBasicTier}
                connectedApps={connectedApps}
                connectingApp={connectingApp}
                onConnectApp={handleConnectAppFromCoin}
                messages={messages}
              />
            ))}

            <div ref={threadEndRef} />
          </div>
        </div>

        {/* ── CONTEXT BAR ── */}
        <div className="px-4 py-1.5 shrink-0 md:pl-5 md:pr-4 md:max-w-[520px] md:ml-0" data-testid="command-context-bar">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[var(--text-muted)] flex items-center gap-1.5">
              {activeOrganizationName && <span className="text-[var(--text-secondary)]">{activeOrganizationName}</span>}
              {activeOrganizationName && <span className="opacity-40">·</span>}
              <button
                onClick={() => setShowAgentList(prev => !prev)}
                className="hover:text-[var(--text-primary)] transition-colors cursor-pointer underline decoration-dotted underline-offset-2"
                data-testid="agents-assigned-toggle"
              >
                {workerCount || 0} worker{(workerCount || 0) !== 1 ? 's' : ''}
              </button>
              {hasVoiceAgents && (
                <>
                  <span className="opacity-40">·</span>
                  <span className="flex items-center gap-0.5"><Microphone size={10} /> Voice enabled</span>
                </>
              )}
              {workflowContext && (
                <>
                  <span className="opacity-40">·</span>
                  <span className="text-[#00E5C0] flex items-center gap-0.5"><FlowArrow size={10} /> Editing: {workflowContext.workerName}</span>
                </>
              )}
            </p>
            {showAgentList && (
              <button onClick={() => setShowAgentList(false)} className="text-[var(--text-muted)] hover:text-white transition-colors p-0.5">
                <X size={12} />
              </button>
            )}
          </div>
          <AnimatePresence>
            {showAgentList && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-2 max-h-[180px] overflow-y-auto space-y-1 scrollbar-thin" data-testid="agents-assigned-list">
                  {/* Workers */}
                  {workers.filter(w => !w.automation_type?.includes('voice') && !w.automation_type?.includes('orion')).length > 0 && (
                    <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wider pt-1 pb-0.5 px-2">Workers</p>
                  )}
                  {workers.filter(w => !w.automation_type?.includes('voice') && !w.automation_type?.includes('orion')).slice(0, 20).map(w => {
                    const wId = w.id || w._id;
                    return (
                      <div
                        key={wId}
                        className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--surface-2)] transition-colors group"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${w.status === 'active' ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                          <span className="text-xs text-[var(--text-secondary)] truncate">{w.name}</span>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button
                            onClick={() => { setShowAgentList(false); closeCommand(); routerNavigate(`/workers/${wId}`); }}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-white hover:border-[var(--border-default)] transition-all"
                            data-testid={`worker-view-${wId}`}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Voice Agents */}
                  {workers.filter(w => w.automation_type?.includes('voice')).length > 0 && (
                    <p className="text-[9px] font-semibold text-[var(--text-muted)] uppercase tracking-wider pt-2 pb-0.5 px-2">Voice Agents</p>
                  )}
                  {workers.filter(w => w.automation_type?.includes('voice')).slice(0, 10).map(w => {
                    const wId = w.id || w._id;
                    return (
                      <div key={wId} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--surface-2)] transition-colors group">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${w.status === 'active' ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                          <span className="text-xs text-[var(--text-secondary)] truncate">{w.name}</span>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button onClick={() => { setShowAgentList(false); closeCommand(); routerNavigate(`/workers/${wId}`); }}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-white hover:border-[var(--border-default)] transition-all">
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {workers.length === 0 && (
                    <p className="text-xs text-[var(--text-muted)] py-2 px-2">No workers yet. Create one using Command.</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── QUICK ACTIONS (edit mode only) ── */}
        {editingAgent && (
          <QuickActionChips
            workflowState={editingAgent.workflow_state}
            onAction={(text) => {
              setCommandInput(text);
              // Auto-submit via ref
              setTimeout(() => {
                composerRef.current?.focus({ preventScroll: true });
              }, 50);
            }}
          />
        )}

        {/* ── CONTEXT SUGGESTION CHIPS ── */}
        {!editingAgent && (
          <SuggestionChips
            onCommand={(cmd) => streamCommandMessage(cmd)}
            currentRoute={window.location.pathname}
            workflowContext={workflowContext}
          />
        )}

        {/* ── COMPOSER ── */}
        <div className="px-4 pt-1.5 pb-4 border-t border-[var(--border-subtle)] shrink-0 md:pl-5 md:pr-4 md:max-w-[520px] md:ml-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }} data-testid="command-composer">
          {/* Recording indicator */}
          {commandBarListening && (
            <div className="mb-2 flex items-center gap-2 px-3 py-2 md:py-1.5 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.15)]">
              <div className="w-2.5 h-2.5 md:w-2 md:h-2 rounded-full bg-[#EF4444] animate-pulse" />
              <span className="text-sm md:text-xs text-[#EF4444]">Listening — release to send</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={composerRef}
                value={commandInput}
                onChange={(e) => {
                  setCommandInput(e.target.value);
                  // Auto-resize
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                placeholder={editingAgent ? `Describe changes to ${editingAgent.agent_name}...` : selectedAgent ? `Message about ${selectedAgent.name}...` : 'Ask, build, or run anything...'}
                rows={1}
                className="w-full min-h-[48px] md:min-h-[80px] max-h-[100px] md:max-h-[140px] px-4 py-3 md:py-4 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-xl text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)] focus:shadow-[0_0_0_3px_rgba(99,102,241,0.1)] transition-all resize-none leading-relaxed"
                style={{ height: window.innerWidth < 768 ? '48px' : '80px' }}
                data-testid="command-input"
              />
            </div>

            <CommandBarMic
              onTranscript={handleVoiceTranscript}
              isListening={commandBarListening}
              setIsListening={setCommandBarListening}
              onMicActivated={stopTTS}
            />

            <button
              type="submit"
              disabled={!commandInput.trim()}
              className={`p-3 md:p-2.5 rounded-xl transition-all shrink-0 ${
                commandInput.trim()
                  ? 'bg-[var(--brand-primary)] text-white hover:opacity-90 shadow-md'
                  : 'bg-[var(--surface-3)] text-[var(--text-muted)]'
              }`}
              title="Send (Enter)"
              data-testid="command-send"
            >
              <PaperPlaneRight size={16} weight={commandInput.trim() ? 'fill' : 'regular'} />
            </button>
          </form>

          {/* Speaking indicator bar */}
          {voiceModeEnabled && isSpeaking && (
            <div className="flex items-center gap-2 px-3 py-2 md:py-1.5 mt-1.5 rounded-lg bg-[rgba(139,92,246,0.06)] border border-[rgba(139,92,246,0.12)]"
              data-testid="voice-speaking-indicator">
              <div className="flex gap-0.5 items-end h-4 md:h-3">
                <div className="w-1 md:w-0.5 bg-[#8B5CF6] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '0ms' }} />
                <div className="w-1 md:w-0.5 bg-[#8B5CF6] rounded-full animate-pulse" style={{ height: '70%', animationDelay: '150ms' }} />
                <div className="w-1 md:w-0.5 bg-[#8B5CF6] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '300ms' }} />
                <div className="w-1 md:w-0.5 bg-[#8B5CF6] rounded-full animate-pulse" style={{ height: '55%', animationDelay: '450ms' }} />
                <div className="w-1 md:w-0.5 bg-[#8B5CF6] rounded-full animate-pulse" style={{ height: '85%', animationDelay: '200ms' }} />
              </div>
              <span className="text-xs md:text-[10px] text-[#8B5CF6] font-medium flex-1">Speaking...</span>
              <button
                onClick={stopTTS}
                className="text-xs md:text-[10px] px-3 md:px-2 py-1 md:py-0.5 rounded border border-[rgba(139,92,246,0.2)] text-[#8B5CF6] hover:bg-[rgba(139,92,246,0.08)] transition-colors"
                data-testid="voice-stop-speaking"
              >
                Stop
              </button>
            </div>
          )}

          {/* Voice mode hint */}
          {voiceModeEnabled && !isSpeaking && (
            <div className="flex items-center gap-1.5 mt-1" data-testid="voice-mode-hint">
              <SpeakerHigh size={10} weight="fill" className="text-[#8B5CF6]" />
              <span className="text-[10px] md:text-[9px] text-[#8B5CF6] opacity-70">
                Voice mode {isMuted ? '(muted)' : 'on'} · Hold mic to speak
              </span>
            </div>
          )}

          {/* Keyboard hints — desktop only */}
          <p className="hidden md:block text-[10px] text-[var(--text-muted)] mt-2 opacity-60">
            <kbd className="px-1 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-muted)] font-mono text-[9px]">Enter</kbd> send · <kbd className="px-1 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-muted)] font-mono text-[9px]">Shift+Enter</kbd> new line · <kbd className="px-1 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-muted)] font-mono text-[9px]">⌘K</kbd> toggle
          </p>
        </div>
        </>
        )}

        {/* ── DETAIL DRAWER (overlay) ── */}
        <DetailDrawer
          isOpen={drawerOpen}
          agent={drawerAgent}
          onClose={() => { setDrawerOpen(false); setDrawerAgent(null); }}
          onRun={handleRunAgent}
          isBasicTier={isBasicTier}
        />

        {/* ── EXECUTION LOCKED MODAL ── */}
        <ExecutionLockedModal
          isOpen={showExecutionLocked}
          onClose={() => setShowExecutionLocked(false)}
          onUpgrade={handleUpgrade}
        />

        {/* ── EXPORT MODAL ── */}
        {showExportModal && lastRunInfo?.sessionId && (
          <UniversalExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            sessionId={lastRunInfo.sessionId}
            agentName={lastRunInfo.agentName}
          />
        )}

        {/* ── UNDO TOAST (sticky 30s window after successful modifications) ── */}
        <UndoToast
          visible={!!undoToast}
          summary={undoToast?.summary}
          onDismiss={() => setUndoToast(null)}
          onUndo={async () => {
            if (!undoToast?.agent_id) return;
            try {
              const resp = await fetch(`${BACKEND_URL}/api/command/undo-modify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ agent_id: undoToast.agent_id }),
              });
              if (resp.ok) {
                const data = await resp.json().catch(() => ({}));
                pushMessage({
                  id: nextId(),
                  type: MSG.MODIFICATION,
                  content: '',
                  modPhase: 'undone',
                  modData: { message: data.message || 'Reverted last change.', agent_name: data.agent_name || '' },
                  modError: null,
                  timestamp: Date.now(),
                });
                notifyAgentModified();
              } else {
                const e = await resp.json().catch(() => ({}));
                pushMessage(createMessage(MSG.SYSTEM, e.detail || 'Could not undo. The change may already be saved.'));
              }
            } catch (err) {
              pushMessage(createMessage(MSG.SYSTEM, 'Undo failed. Please try again.'));
            }
          }}
        />
      </div>
    </div>
  );

  return splitPane ? panelContent : createPortal(panelContent, document.body);
};

// ═══════════════════════════════════════════════════════════════
// COMMAND LAUNCHER FAB
// ═══════════════════════════════════════════════════════════════

export const CommandLauncher = () => {
  const { isOpen, toggleCommand } = useCommand();
  if (isOpen) return null;

  return createPortal(
    <button
      onClick={toggleCommand}
      className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[9998] flex items-center gap-2 px-5 py-3.5 md:px-4 md:py-3 rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white font-medium shadow-lg shadow-[var(--brand-primary)]/25 hover:shadow-xl hover:scale-[1.03] transition-all"
      style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
      title="Open Command (⌘K)"
      data-testid="command-launcher"
    >
      <Lightning size={20} weight="fill" className="md:w-[18px] md:h-[18px]" />
      <span className="text-sm">Command</span>
    </button>,
    document.body
  );
};

export default Command;
